import { Toaster as Sonner } from 'sonner';

import {
  APP_TOAST_DEFAULT_DURATION_MS,
  SonnerToastDrains,
} from '@/components/patterns/toast-drain/sonner-toast-drains';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const baseToastClassNames = {
  toast:
    'group toast relative overflow-hidden border border-border bg-popover text-popover-foreground shadow-lg',
  title: 'text-sm font-semibold text-foreground',
  description: 'text-xs text-foreground-secondary',
  actionButton: 'bg-transparent font-medium text-foreground',
  cancelButton: 'bg-transparent text-muted-foreground',
  closeButton: 'border-none bg-transparent text-muted-foreground hover:bg-muted',
} as const;

/**
 * Every `toast()` in the app renders here. The card is a neutral raised surface:
 * status color stays on the icon, the description stays readable, and the close
 * control sits inside the card. Do not paint success/error/warning/info fills
 * back onto the card (UI-24).
 *
 * Auto-dismiss defaults to `APP_TOAST_DEFAULT_DURATION_MS` with a bottom drain.
 * Pass `duration: Infinity` on a toast to keep it until the reader closes it (no drain).
 */
const Toaster = ({
  duration = APP_TOAST_DEFAULT_DURATION_MS,
  toastOptions,
  ...props
}: ToasterProps) => {
  return (
    <>
      <Sonner
        className="toaster group"
        position="bottom-right"
        visibleToasts={1}
        expand={false}
        closeButton
        duration={duration}
        toastOptions={{
          duration,
          ...toastOptions,
          classNames: {
            ...baseToastClassNames,
            ...toastOptions?.classNames,
          },
        }}
        {...props}
      />
      <SonnerToastDrains defaultDurationMs={duration} position={props.position ?? 'bottom-right'} />
    </>
  );
};

/** The toast API for app code: the lint rule sends `sonner` imports here. */
export { toast } from 'sonner';
export { APP_TOAST_DEFAULT_DURATION_MS, Toaster };
